import { Link } from 'react-router-dom';
import { ArrowRight, Check, Star, MessageCircle, Sparkles, Users, Building2 } from 'lucide-react';
import MarketingLayout from './shared/MarketingLayout';
import { PageHero, Section, SectionHeader, CTAStrip, FAQAccordion } from './shared/blocks';
import { TrustBadges } from './shared/PremiumBlocks';
import SEO from './shared/SEO';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { PRICING_TIERS, BRAND, FAQ_ITEMS } from './content';

function PriceTag({ price }) {
  if (price.custom) return <div className="text-2xl font-extrabold">{price.custom}</div>;
  return (
    <div>
      <span className="text-4xl font-extrabold">₹{price.inr}</span>
      <span className="ml-1 text-sm text-slate-500">/ {price.suffix}</span>
    </div>
  );
}

function TierCard({ tier }) {
  const isFeatured = tier.highlight;
  return (
    <div
      className={`relative rounded-2xl border p-6 sm:p-7 flex flex-col bg-white dark:bg-mk-navy transition-all hover:shadow-xl hover:-translate-y-0.5 ${
        isFeatured
          ? 'border-mk-blue shadow-xl shadow-mk-line/40 dark:shadow-mk-navy/20 ring-1 ring-mk-blue/30'
          : 'border-mk-line dark:border-mk-edge'
      }`}
      data-testid={`pricing-tier-${tier.id}`}
    >
      {isFeatured && (
        <Badge className="absolute -top-3 left-6 bg-mk-blue text-white border-0 gap-1"><Star className="h-3 w-3" /> Most popular</Badge>
      )}
      {tier.comingSoon && (
        <Badge variant="outline" className="absolute -top-3 right-6 bg-amber-50 text-amber-800 border-amber-200">Coming soon</Badge>
      )}
      <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">{tier.audience}</div>
      <h3 className="text-2xl font-bold">{tier.name}</h3>
      <div className="mt-4 mb-5"><PriceTag price={tier.price} /></div>
      <ul className="space-y-2 text-sm flex-1">
        {tier.features.map((f) => (
          <li key={f} className="flex items-start gap-2">
            <Check className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" /> {f}
          </li>
        ))}
      </ul>
      <div className="mt-6">
        {tier.cta.wa ? (
          <a href={BRAND.whatsappLink(tier.cta.wa)} target="_blank" rel="noopener noreferrer" data-testid={`pricing-cta-${tier.id}`}>
            <Button className={`w-full h-11 font-semibold ${
              isFeatured ? 'bg-mk-blue hover:bg-mk-deep text-white' : 'bg-mk-navy hover:bg-slate-800 text-white dark:bg-slate-100 dark:text-mk-ink dark:hover:bg-white'
            }`}>
              <MessageCircle className="mr-1.5 h-4 w-4" /> {tier.cta.label}
            </Button>
          </a>
        ) : (
          <Link to={tier.cta.href} data-testid={`pricing-cta-${tier.id}`}>
            <Button className={`w-full h-11 font-semibold ${
              isFeatured ? 'bg-mk-blue hover:bg-mk-deep text-white' : 'bg-mk-navy hover:bg-slate-800 text-white dark:bg-slate-100 dark:text-mk-ink dark:hover:bg-white'
            }`}>
              {tier.cta.label} <ArrowRight className="ml-1.5 h-4 w-4" />
            </Button>
          </Link>
        )}
      </div>
    </div>
  );
}

export default function PricingPage() {
  const individuals = PRICING_TIERS.filter(t => t.audience === 'Individual Teachers');
  const schools     = PRICING_TIERS.filter(t => t.audience !== 'Individual Teachers');

  return (
    <MarketingLayout>
      <SEO
        title="Pricing | Free, Starter ₹299, Pro ₹499, Elite ₹999 + School Pilot"
        description="Six pricing tiers for ActanonEdu — Free, Starter (₹299/mo), Pro (₹499/mo), Elite (₹999/mo) for individual teachers, plus a free 30-day School Pilot and custom Enterprise plans for chains and districts."
        path="/pricing"
        keywords={['ActanonEdu pricing', 'teacher AI pricing', 'school ERP pricing India', 'lesson plan generator price']}
        breadcrumbs={[{ name: 'Home', url: '/' }, { name: 'Pricing', url: '/pricing' }]}
        faq={FAQ_ITEMS.slice(0, 5)}
      />
      <PageHero
        eyebrow="Pricing"
        title="Honest pricing."
        accent="No hidden tiers."
        subtitle="Four tiers for individual teachers — from Free to Elite. A free 30-day Pilot for schools. Custom for chains & districts."
        testid="pricing-hero"
      >
        <TrustBadges />
      </PageHero>

      {/* INDIVIDUAL TEACHERS — 4 tiers */}
      <Section>
        <div className="mb-8 flex items-center gap-2.5">
          <Users className="h-5 w-5 text-mk-blue" />
          <h2 className="text-xl sm:text-2xl font-bold tracking-tight">For Individual Teachers</h2>
          <span className="text-sm text-slate-500">— pick your tier, upgrade or downgrade any time.</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="pricing-individual-grid">
          {individuals.map((t) => <TierCard key={t.id} tier={t} />)}
        </div>
      </Section>

      {/* SCHOOLS — 2 tiers */}
      <Section tone="muted">
        <div className="mb-8 flex items-center gap-2.5">
          <Building2 className="h-5 w-5 text-emerald-600" />
          <h2 className="text-xl sm:text-2xl font-bold tracking-tight">For Schools, Chains & Districts</h2>
          <span className="text-sm text-slate-500">— pilot first, scale later.</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="pricing-school-grid">
          {schools.map((t) => <TierCard key={t.id} tier={t} />)}
        </div>
      </Section>

      {/* Plan comparison helper */}
      <Section>
        <div className="rounded-2xl bg-gradient-to-br from-mk-ice via-mk-ice to-mk-ice dark:from-mk-navy/15 dark:via-mk-navy/15 dark:to-mk-navy/15 border border-mk-line/70 dark:border-mk-navy/40 p-6 sm:p-8">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <Sparkles className="h-5 w-5 text-mk-blue mb-1" />
              <h3 className="text-lg sm:text-xl font-bold">Not sure which tier to pick?</h3>
              <p className="text-sm text-mk-muted dark:text-slate-300 mt-1">Start Free. Upgrade when you hit your first ceiling — no penalty, prorated billing.</p>
            </div>
            <Link to="/signup">
              <Button className="bg-mk-blue hover:bg-mk-deep text-white h-11 px-5 font-semibold">Start Free <ArrowRight className="ml-1.5 h-4 w-4" /></Button>
            </Link>
          </div>
        </div>
      </Section>

      <Section tone="muted">
        <SectionHeader eyebrow="Pricing FAQ" title="Common questions before buying." />
        <FAQAccordion items={FAQ_ITEMS.slice(0, 5)} testid="pricing-faq" />
      </Section>

      <CTAStrip
        title="Start with the Free tier today."
        primary={{ label: 'Sign up free', href: '/signup' }}
        secondary={{ label: 'Founder chat', wa: 'Hi Founder! I want to discuss pricing.' }}
        testid="pricing-final-cta"
      />
    </MarketingLayout>
  );
}
